import { useEffect, useState, useCallback } from 'react'
import { Outlet, useNavigate } from 'react-router'
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar'
import { Separator } from '@/components/ui/separator'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Search, User } from 'lucide-react'
import { AdminSidebar } from './AdminSidebar'
import { NotificationBell } from '@/components/notifications/NotificationBell'
import { fetchAdminUsers, type AdminUser } from '@/lib/api/admin-users'
import { adminTierLabel } from '@/lib/tierLadder'

export function AdminLayout() {
  const [cmdkOpen, setCmdkOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<AdminUser[]>([])
  const [searching, setSearching] = useState(false)
  const navigate = useNavigate()

  // Cmd+K keyboard shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setCmdkOpen((open) => !open)
      }
    }
    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [])

  // Debounced search
  useEffect(() => {
    if (!search || search.length < 2) {
      setResults([])
      return
    }
    const timer = setTimeout(async () => {
      setSearching(true)
      try {
        const data = await fetchAdminUsers({ search, pageSize: 8 })
        setResults(data.users)
      } catch {
        setResults([])
      }
      setSearching(false)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  const selectUser = useCallback((userId: string) => {
    setCmdkOpen(false)
    setSearch('')
    setResults([])
    navigate(`/users/${userId}`)
  }, [navigate])

  return (
    <SidebarProvider>
      <AdminSidebar />
      <SidebarInset>
        <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger />
          <Separator orientation="vertical" className="h-6" />
          <button
            onClick={() => setCmdkOpen(true)}
            className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted/80"
          >
            <Search className="h-3.5 w-3.5" />
            <span>Search users...</span>
            <kbd className="ml-4 rounded border bg-background px-1.5 py-0.5 text-[10px] font-mono">
              Ctrl+K
            </kbd>
          </button>
          <div className="ml-auto flex items-center gap-1">
            <NotificationBell />
          </div>
        </header>
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </SidebarInset>

      <CommandDialog open={cmdkOpen} onOpenChange={setCmdkOpen}>
        <CommandInput
          placeholder="Search by name, email, or user ID..."
          value={search}
          onValueChange={setSearch}
        />
        <CommandList>
          <CommandEmpty>
            {searching ? 'Searching...' : search.length < 2 ? 'Type to search...' : 'No users found.'}
          </CommandEmpty>
          {results.length > 0 && (
            <CommandGroup heading="Users">
              {results.map((user) => (
                <CommandItem
                  key={user.id}
                  value={`${user.email} ${user.display_name ?? ''}`}
                  onSelect={() => selectUser(user.id)}
                >
                  <User className="mr-2 h-4 w-4 text-muted-foreground" />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">
                      {user.display_name || user.email}
                    </span>
                    {user.display_name && (
                      <span className="text-xs text-muted-foreground">{user.email}</span>
                    )}
                  </div>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {adminTierLabel(user.subscription_tier, user.legacy_caps)}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </SidebarProvider>
  )
}
